import { ref, computed } from 'vue';
import { useToast } from '@/composables/useToast';

/**
 * Dashboard Statistics Cache Service
 * 
 * This service implements intelligent caching for dashboard statistics
 * to reduce database load from frequent dashboard queries while ensuring
 * data freshness through smart invalidation strategies.
 */
class DashboardCacheService {
  constructor() {
    this.cache = new Map();
    this.cacheMetadata = new Map();
    this.isRefreshing = ref(false);
    this.lastRefreshTime = ref(null);

    // Cache configuration
    this.config = {
      // TTL for different data types (in milliseconds)
      ttl: {
        dashboard_stats: 5 * 60 * 1000,      // 5 minutes
        payment_stats: 10 * 60 * 1000,      // 10 minutes
        order_stats: 5 * 60 * 1000,         // 5 minutes
        chart_data: 15 * 60 * 1000,         // 15 minutes
        reports: 30 * 60 * 1000,            // 30 minutes
        quick_stats: 2 * 60 * 1000,         // 2 minutes
      },

      // Background refresh timing
      backgroundRefresh: {
        enabled: true,
        threshold: 0.8, // Refresh when 80% of TTL has elapsed
        batchSize: 5,   // Max items to refresh in one batch
      },

      // Memory management
      maxCacheSize: 1000,
      cleanupInterval: 30 * 60 * 1000, // 30 minutes
    };

    // Performance tracking
    this.stats = {
      hits: 0,
      misses: 0,
      refreshes: 0,
      errors: 0,
      totalRequests: 0,
      backgroundRefreshes: 0,
    };

    this.toast = null; // Injected via useDashboardCache
    this.isInitialized = false;

    console.log('[DashboardCache] Initialized with intelligent caching');
  }

  /**
   * Initialize the cache service
   */
  initialize() {
    if (this.isInitialized) return;

    this.startBackgroundRefresh();
    this.startCleanupScheduler();
    this.isInitialized = true;

    console.log('[DashboardCache] Service initialized');
  }

  /**
   * Get cached data or fetch from source
   * @param {string} key - Cache key
   * @param {Function} fetchFn - Function to fetch data if not cached
   * @param {Object} options - Caching options
   */
  async get(key, fetchFn, options = {}) {
    const cacheKey = this.generateCacheKey(key, options);
    const startTime = Date.now();

    this.stats.totalRequests++;

    try {
      // Check if we have cached data
      const cachedData = this.getCachedData(cacheKey);
      if (cachedData !== null) {
        this.stats.hits++;
        console.log(`[DashboardCache] Cache hit for ${cacheKey}`);

        // Schedule background refresh if needed
        this.scheduleBackgroundRefresh(cacheKey, fetchFn, options);

        return cachedData;
      }

      // Cache miss - fetch fresh data
      this.stats.misses++;
      console.log(`[DashboardCache] Cache miss for ${cacheKey}, fetching...`);

      const freshData = await this.fetchWithRetry(fetchFn, key);

      // Cache the fresh data
      this.setCachedData(cacheKey, freshData, options);

      const fetchTime = Date.now() - startTime;
      console.log(`[DashboardCache] Fetched ${cacheKey} in ${fetchTime}ms`);

      return freshData;

    } catch (error) {
      this.stats.errors++;
      console.error(`[DashboardCache] Error fetching ${cacheKey}:`, error);

      // Try to return stale data as fallback
      const staleData = this.getStaleData(cacheKey);
      if (staleData !== null) {
        console.log(`[DashboardCache] Returning stale data for ${cacheKey}`);
        if (this.toast) this.toast.warning('Using cached data - refresh in progress');
        return staleData;
      }

      throw error;
    }
  }

  /**
   * Fetch data with retry logic
   */
  async fetchWithRetry(fetchFn, key, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await fetchFn();
      } catch (error) {
        console.warn(`[DashboardCache] Fetch attempt ${i + 1} failed for ${key}:`, error);

        if (i === retries - 1) {
          throw error;
        }

        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, i), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Set data in cache
   */
  setCachedData(key, data, options = {}) {
    const ttl = options.ttl || this.getTTL(key);
    const expiresAt = Date.now() + ttl;

    this.cache.set(key, {
      data,
      expiresAt,
      createdAt: Date.now(),
      accessCount: 0,
    });

    this.cacheMetadata.set(key, {
      ttl,
      type: options.type || 'dashboard_stats',
      priority: options.priority || 'normal',
      refreshable: options.refreshable !== false,
    });

    console.log(`[DashboardCache] Cached ${key} with TTL ${ttl}ms`);

    // Trigger cache size management
    this.manageCacheSize();
  }

  /**
   * Get cached data if valid
   */
  getCachedData(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    cached.accessCount++;

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      console.log(`[DashboardCache] Cache expired for ${key}`);
      return null;
    }

    return cached.data;
  }

  /**
   * Get stale data (expired but still in cache)
   */
  getStaleData(key) {
    const cached = this.cache.get(key);
    return cached ? cached.data : null;
  }

  /**
   * Generate cache key
   */
  generateCacheKey(key, options = {}) {
    const parts = [key];

    if (options.filters) {
      parts.push(JSON.stringify(options.filters));
    }

    if (options.userId) {
      parts.push(`user:${options.userId}`);
    }

    if (options.dateRange) {
      parts.push(`date:${options.dateRange.start}-${options.dateRange.end}`);
    }

    return parts.join('|');
  }

  /**
   * Get TTL for cache type
   */
  getTTL(key) {
    // Determine cache type from key
    if (key.includes('dashboard_stats')) return this.config.ttl.dashboard_stats;
    if (key.includes('payment_stats')) return this.config.ttl.payment_stats;
    if (key.includes('order_stats')) return this.config.ttl.order_stats;
    if (key.includes('chart_data')) return this.config.ttl.chart_data;
    if (key.includes('reports')) return this.config.ttl.reports;
    if (key.includes('quick_stats')) return this.config.ttl.quick_stats;

    return this.config.ttl.dashboard_stats; // Default
  }

  /**
   * Schedule background refresh if needed
   */
  scheduleBackgroundRefresh(key, fetchFn, options) {
    if (!this.config.backgroundRefresh.enabled) return;

    const cached = this.cache.get(key);
    const metadata = this.cacheMetadata.get(key);

    if (!cached || !metadata || !metadata.refreshable) return;

    // Check if we should refresh based on age
    const age = Date.now() - cached.createdAt;
    const refreshThreshold = metadata.ttl * this.config.backgroundRefresh.threshold;

    if (age > refreshThreshold) {
      console.log(`[DashboardCache] Scheduling background refresh for ${key}`);

      // Use setTimeout to avoid blocking
      setTimeout(() => {
        this.backgroundRefresh(key, fetchFn, options);
      }, 100);
    }
  }

  /**
   * Perform background refresh
   */
  async backgroundRefresh(key, fetchFn, options) {
    try {
      this.stats.backgroundRefreshes++;
      console.log(`[DashboardCache] Background refreshing ${key}`);

      const freshData = await fetchFn();
      this.setCachedData(key, freshData, options);

      console.log(`[DashboardCache] Background refresh completed for ${key}`);

    } catch (error) {
      console.error(`[DashboardCache] Background refresh failed for ${key}:`, error);
    }
  }

  /**
   * Start background refresh scheduler
   */
  startBackgroundRefresh() {
    if (!this.config.backgroundRefresh.enabled) return;

    setInterval(() => {
      this.performScheduledRefreshes();
    }, 60000); // Check every minute

    console.log('[DashboardCache] Background refresh scheduler started');
  }

  /**
   * Perform scheduled refreshes
   */
  async performScheduledRefreshes() {
    const now = Date.now();
    const refreshCandidates = [];

    // Find items that need refreshing
    for (const [key, metadata] of this.cacheMetadata.entries()) {
      if (!metadata.refreshable) continue;

      const cached = this.cache.get(key);
      if (!cached) continue;

      const age = now - cached.createdAt;
      const refreshThreshold = metadata.ttl * this.config.backgroundRefresh.threshold;

      if (age > refreshThreshold) {
        refreshCandidates.push({
          key,
          priority: metadata.priority,
          accessCount: cached.accessCount,
          age
        });
      }
    }

    if (refreshCandidates.length > 0) {
      // Sort by priority and access count
      refreshCandidates.sort((a, b) => {
        if (a.priority !== b.priority) {
          return a.priority === 'high' ? -1 : 1;
        }
        return b.accessCount - a.accessCount;
      });

      // Refresh up to batch size
      const toRefresh = refreshCandidates.slice(0, this.config.backgroundRefresh.batchSize);
      console.log(`[DashboardCache] Background refreshing ${toRefresh.length} items`);

      // Note: We'd need to store the fetch functions to do actual refresh
      // For now, we just log the intent
      toRefresh.forEach(item => {
        console.log(`[DashboardCache] Would refresh ${item.key}`);
      });
    }
  }

  /**
   * Invalidate cache entries
   */
  invalidate(pattern) {
    let invalidated = 0;

    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        this.cacheMetadata.delete(key);
        invalidated++;
      }
    }

    console.log(`[DashboardCache] Invalidated ${invalidated} entries matching "${pattern}"`);
  }

  /**
   * Invalidate on data changes
   */
  invalidateOnChange(changeType, entityId = null) {
    const patterns = {
      'order_created': ['order_stats', 'dashboard_stats'],
      'payment_received': ['payment_stats', 'dashboard_stats'],
      'order_updated': ['order_stats', 'dashboard_stats'],
      'data_import': ['dashboard_stats', 'reports'],
    };

    const patternsToInvalidate = patterns[changeType] || [];

    patternsToInvalidate.forEach(pattern => {
      this.invalidate(pattern);
    });

    console.log(`[DashboardCache] Invalidated cache for change type: ${changeType}`);
  }

  /**
   * Manage cache size
   */
  manageCacheSize() {
    if (this.cache.size <= this.config.maxCacheSize) return;

    console.log(`[DashboardCache] Cache size (${this.cache.size}) exceeds limit, cleaning up`);

    // Get all entries with metadata
    const entries = Array.from(this.cache.entries()).map(([key, value]) => ({
      key,
      value,
      metadata: this.cacheMetadata.get(key),
    }));

    // Sort by access count (LRU-like) and expiration time
    entries.sort((a, b) => {
      if (a.value.accessCount !== b.value.accessCount) {
        return a.value.accessCount - b.value.accessCount;
      }
      return a.value.expiresAt - b.value.expiresAt;
    });

    // Remove oldest/least accessed entries
    const toRemove = entries.slice(0, this.cache.size - this.config.maxCacheSize);
    toRemove.forEach(entry => {
      this.cache.delete(entry.key);
      this.cacheMetadata.delete(entry.key);
    });

    console.log(`[DashboardCache] Removed ${toRemove.length} entries from cache`);
  }

  /**
   * Start cleanup scheduler
   */
  startCleanupScheduler() {
    setInterval(() => {
      this.performCleanup();
    }, this.config.cleanupInterval);

    console.log('[DashboardCache] Cleanup scheduler started');
  }

  /**
   * Perform cache cleanup
   */
  performCleanup() {
    const now = Date.now();
    let cleaned = 0;

    // Remove expired entries
    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiresAt) {
        this.cache.delete(key);
        this.cacheMetadata.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      console.log(`[DashboardCache] Cleaned up ${cleaned} expired entries`);
    }

    // Force garbage collection hint
    this.manageCacheSize();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const hitRate = this.stats.totalRequests > 0
      ? ((this.stats.hits / this.stats.totalRequests) * 100).toFixed(1)
      : '0';

    return {
      ...this.stats,
      hitRate: `${hitRate}%`,
      cacheSize: this.cache.size,
      memoryUsage: this.estimateMemoryUsage(),
    };
  }

  /**
   * Estimate memory usage
   */
  estimateMemoryUsage() {
    let totalSize = 0;

    for (const [key, value] of this.cache.entries()) {
      totalSize += JSON.stringify(value.data).length;
      totalSize += key.length;
    }

    return `${(totalSize / 1024).toFixed(1)} KB`;
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.cacheMetadata.clear();
    console.log('[DashboardCache] Cache cleared');
  }
}

// Create singleton instance
const dashboardCache = new DashboardCacheService();

// Export composable function
export function useDashboardCache() {
  const toast = useToast();
  if (!dashboardCache.toast) {
    dashboardCache.toast = toast;
  }

  return {
    // Core functionality
    get: (key, fetchFn, options) => dashboardCache.get(key, fetchFn, options),
    set: (key, data, options) => dashboardCache.setCachedData(key, data, options),
    invalidate: (pattern) => dashboardCache.invalidate(pattern),
    invalidateOnChange: (changeType, entityId) => dashboardCache.invalidateOnChange(changeType, entityId),
    clear: () => dashboardCache.clear(),

    // State
    isRefreshing: computed(() => dashboardCache.isRefreshing.value),
    lastRefreshTime: computed(() => dashboardCache.lastRefreshTime.value),

    // Statistics
    getStats: () => dashboardCache.getStats(),

    // Management
    initialize: () => dashboardCache.initialize(),
  };
}

export default dashboardCache;